import React, {
	useMemo, useEffect, useState, useRef, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Avatar from '../../general-components/avatar';
import * as SessionActions from '../../../stores/actions/session';
import { showDetailsBack } from '../../../stores/actions/main';
import { Invitation } from '../../../stores/reducers/invitations';
import Logo from '../../../images/logo-white.png';
import Dialog from '../../general-components/dialog';
import NotificationCenter from './notification-center';
import * as HomeActions from '../../../stores/actions/home';
import { HomeNotification, HOME_ROUTES } from '../../../stores/reducers/home';
import { SetNotificationsSeen } from '../../../connection/home';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import { META_MENU_ROUTES, META_MENU_STATES } from '../../../stores/reducers/session';
import { getChannelLogoFromMeeting, logOut } from '../../../utils/utils';
import AvatarBubbleRow from '../../general-components/avatar-bubble-row';
import { APP } from '../../../stores/session-status';
import { GreenroomState } from '../../../stores/types/types';
import TellyChannelLogo from '../../../images/app-store-icon.png';
import HeaderLeftControls from '../../header-components/header-left-controls';

function getNotificationId(notification: HomeNotification): number | undefined {
	return notification.notification;
}

export default function HomeHeader() {
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const metaMenuState = useSelector((state: GreenroomState) => state.SessionReducer.metaMenuState);
	const metaMenuActiveRoute = useSelector(
		(state: GreenroomState) => state.SessionReducer.metaMenuActiveRoute,
	);
	const appSignedInWith = useSelector(
		(state: GreenroomState) => state.SessionReducer.appSignedInWith,
	);
	const showSessions = useSelector((state: GreenroomState) => state.SessionReducer.showSessions);
	const invitations = useSelector((state: GreenroomState) => state.InvitationsReducer.invitations);
	const notifications = useSelector((state: GreenroomState) => state.HomeReducer.notifications);
	const selectedChannel = useSelector((state: GreenroomState) => state.HomeReducer.selectedChannel);
	const currentRoute = useSelector((state: GreenroomState) => state.HomeReducer.currentRoute);
	const homeMeeting = useSelector((state: GreenroomState) => state.HomeReducer.homeMeeting);
	const dispatch = useDispatch();
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const [notificationsOpen, setNotificationsOpen] = useState<boolean>(false);
	const dialog = useRef<any>(null);
	const currentNotifications = useRef<HomeNotification[]>([]);

	useEffect(() => {
		currentNotifications.current = notifications;
	}, [notifications]);

	function confirmSignout() {
		dialog.current?.confirm(
			'Are you sure you want to sign out?',
			'You will be taken back to the sign in form.',
			'Sign Out',
			'Cancel',
			(confirmed: boolean) => {
				if (confirmed) {
					logOut(host);
				}
			},
		);
	}

	function goBackToHome() {
		dispatch(showDetailsBack());
	}

	const handleNotificationClose = useCallback(async () => {
		setNotificationsOpen(false);
		if (currentNotifications.current && token) {
			const notificationIDs = currentNotifications.current.map(getNotificationId);
			const cleanNotificationIDs = notificationIDs.filter((id) => !!id) as number[];
			await SetNotificationsSeen(token, cleanNotificationIDs);
		}

		if (token) { dispatch(HomeActions.loadNotifications(token)); }
	}, [currentNotifications, dispatch, token]);

	const thereAreUnseenNotifications = useMemo(
		() => notifications.some((notification: HomeNotification) => notification.has_been_seen === 0),
		[notifications],
	);

	const toggleDrawer = (metaMenuRoute: string) => {
		if (metaMenuState === META_MENU_STATES.CLOSED) {
			dispatch(SessionActions.setMetaMenuRoute(metaMenuRoute));
			dispatch(SessionActions.setMetaMenuState(META_MENU_STATES.OPENING));
		} else if (metaMenuState === META_MENU_STATES.OPEN) {
			if (metaMenuRoute !== metaMenuActiveRoute) {
				dispatch(SessionActions.setMetaMenuRoute(metaMenuRoute));
			} else {
				dispatch(SessionActions.setMetaMenuState(META_MENU_STATES.CLOSING));
			}
		}
	};

	function openStandaloneGreenroomModal(editing: boolean) {
		// If a meeting was edited prior to clicking "New Greenroom". Clear it so the modal is fresh
		if (editing) {
			dispatch(SessionActions.setMeetingToEdit({
				...homeMeeting,
				invites: invitations.map((invite: Invitation) => ({
					...invite.profile,
					watcher_only: invite.watcher_only,
				})),
				show_sessions: showSessions,
			}));
		} else {
			dispatch(SessionActions.setMeetingToEdit(null));
		}

		dispatch(SessionActions.setMeetingModalOpen(true));
	}

	const currentChannelName: string = useMemo(() => {
		const channel = selectedChannel ? { ...selectedChannel } : { ...homeMeeting };
		return channel?.channel_name || channel?.name || '';
	}, [selectedChannel, homeMeeting]);

	const currentChannelLogo: string = useMemo(() => (
		getChannelLogoFromMeeting(homeMeeting) || TellyChannelLogo
	), [homeMeeting]);

	return (
		<>
			<header>
				{currentRoute !== HOME_ROUTES.DETAILS
					&& <>
						{host
							? (
								<span className="text-light-gray font-size-16 font-weight-bold truncate-overflow" style={{ marginLeft: '-20px', maxWidth: '75%' }}>
									{selectedChannel ? `${selectedChannel.name}` : ''}
								</span>
							) : (
								<img className="logo" src={Logo} alt="" />
							)
						}
					</>
				}
				{currentRoute === HOME_ROUTES.DETAILS && (
					<div className="show-details-header-left">
						<HeaderLeftControls
							channelLogo={currentChannelLogo}
							title={homeMeeting?.title || ''}
							backButtonHandler={goBackToHome}
							altText={currentChannelName}
						/>
					</div>
				)}
				<div className="header-right">
					{currentRoute !== HOME_ROUTES.DETAILS && appSignedInWith === APP.GREENROOM && host && <>
						<button
							onClick={() => openStandaloneGreenroomModal(false)}
							className="padding-button-small corner-round-massive background-green align-items-center button-background-shading cursor-pointer"
						>
							<Icon name={ICONS.ADD} color={COLORS.LIGHT_BLACK} size={12}></Icon>
							<span className="margin-left-small text-dark">Create Show</span>
						</button>
						<div className="divider"></div>
					</>}
					{currentRoute === HOME_ROUTES.DETAILS && host && <>
						{invitations
							&& <AvatarBubbleRow
								invites={invitations.map(({ profile }: Invitation) => profile)}
								size={'thirtytwo'}
								alignEnd={false}
								expandable={true}
							/>
						}
						{appSignedInWith === APP.GREENROOM
							&& <button
								onClick={() => openStandaloneGreenroomModal(true)}
								className="padding-button-small corner-round-massive background-lighter align-items-center button-background-shading cursor-pointer"
							>
								<Icon name={ICONS.EDIT} color={COLORS.LIGHT_GRAY} size={12}></Icon>
								<span className="margin-left-small text-light-gray">Edit</span>
							</button>
						}
						<div className="divider"></div>
					</>}

					<button
						className={classNames('notification-bell', { active: notificationsOpen })}
						onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
							e.stopPropagation();
							setDrawerOpen(false);
							setNotificationsOpen(true);
						}}>
						<Icon name={ICONS.BELL} color={COLORS.WHITE} size={18} />
						{thereAreUnseenNotifications && <div className='new-notification-dot'></div>}
					</button>

					<div style={{ zIndex: 'auto' }}>
						{user && <Avatar
							user={user}
							size="small"
							onClick={() => {
								setDrawerOpen(true);
								setNotificationsOpen(false);
							}}
							noBorder={true}
							selected={drawerOpen}
							popoverExtraTopMargin={true}
						>
							<div className="avatar-options">
								<button className="avatar-option" onClick={() => {
									dispatch(SessionActions.setMetaMenuRoute(META_MENU_ROUTES.PROFILE));
									dispatch(SessionActions.setAvatarPopoverOpen(false));
									toggleDrawer(META_MENU_ROUTES.PROFILE);
								}}>
									<Icon name={ICONS.PRESENTERS} color={COLORS.GRAY} size={13} />Edit Profile
								</button>
								<button className="avatar-option" onClick={() => {
									dispatch(SessionActions.setMetaMenuRoute(META_MENU_ROUTES.PRESENTER_SETTINGS));
									dispatch(SessionActions.setAvatarPopoverOpen(false));
									toggleDrawer(META_MENU_ROUTES.PRESENTER_SETTINGS);
								}}>
									<Icon name={ICONS.MICROPHONE_ON} color={COLORS.GRAY} size={13} />Devices
								</button>
								<button className="avatar-option" onClick={() => {
									confirmSignout();
								}}>
									<Icon name={ICONS.LOG_OUT} color={COLORS.GRAY} size={13} />Sign Out
								</button>
							</div>
						</Avatar>}
					</div>
				</div>
			</header>

			<NotificationCenter
				open={notificationsOpen}
				onClose={handleNotificationClose}
			/>

			<Dialog right={true} ref={dialog} />
		</>

	);
}
